import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, provideAppInitializer, LOCALE_ID, inject } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { AuthService } from 'app/core/auth/auth.service';
import { authInterceptor } from 'app/core/auth/auth.interceptor';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideFuse } from '@fuse';
import { MockApiService } from 'app/mock-api';
import { getPortuguesePaginatorIntl } from 'app/shared/components/paginator/custom-paginator';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { provideNgxMask } from 'ngx-mask';
import { LoaderInterceptorService } from './shared/services/interceptor/loader-interceptor.service';
import { WhiteLabelStateService } from 'app/shared/services/edduca-imob/white-label/white-label-state.service';

registerLocaleData(localePt, 'pt-BR');

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),

    // Detecta o subdomínio e carrega o white-label antes de pintar a app.
    // Não bloqueia se a request demorar/falhar — branding Edduca padrão é o fallback.
    provideAppInitializer(() => {
      const wlState = inject(WhiteLabelStateService);
      const slug = wlState.extrairSlugDoHostname();
      if (!slug) return Promise.resolve();
      return wlState.carregarPorSlug(slug);
    }),

    provideHttpClient(
      withInterceptors([authInterceptor]),
      withInterceptorsFromDi()
    ),    
    provideNgxMask(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ 
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: LuxonDateAdapter },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: { dateInput: 'D' },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        }
      }
    },

    provideFuse({
      mockApi: {
        delay: 0,
        service: MockApiService,
      },
      fuse: {
        layout: 'compact',
        scheme: 'dark',
        screens: { sm: '600px', md: '960px', lg: '1280px', xl: '1440px' },
        theme: 'theme-default',
        themes: [
          { id: 'theme-default', name: 'Default' },
          { id: 'theme-brand', name: 'Brand' },
          { id: 'theme-teal', name: 'Teal' },
          { id: 'theme-rose', name: 'Rose' },
          { id: 'theme-purple', name: 'Purple' },
          { id: 'theme-amber', name: 'Amber' }
        ]
      }
    }),

    provideAuth(),

    provideIcons(),

    { provide: MatPaginatorIntl, useFactory: getPortuguesePaginatorIntl },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptorService, multi: true },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: (overlay: Overlay): MatDialogConfig => ({
        scrollStrategy: overlay.scrollStrategies.noop(),
        hasBackdrop: true
      }),
      deps: [Overlay]
    }
  ]
};
